import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useMetaData } from '../queries/useMetaData.js'
import { createWeeklySnapshot } from '../data/metaApi.js'
import { currentWeekKey, weekKeyOffset } from '../data/metaKeys.js'

/**
 * useAutoSnapshot — runs once per session, checks whether the week just
 * ended has been archived yet, and if not calls the server-side snapshot
 * endpoint.
 *
 * Timing:
 *   - On the first page-load each Monday (or any day thereafter), if
 *     meta.lastSnapshotWeek is NOT the current week's Monday key, we
 *     snapshot the PREVIOUS week's Monday key.
 *   - The endpoint is idempotent: two tabs opening at the same time won't
 *     duplicate — the second call gets { alreadyExisted: true }.
 *
 * Why client-driven rather than a worker cron: client-triggered means any
 * team member's visit can kick it off; a cron would need wrangler.toml
 * config changes and an always-on worker schedule. For a 16-person team
 * this is simpler and just as reliable — someone always visits on Monday.
 *
 * The hook is silent on failure (just logs) — a missed snapshot isn't
 * worth bothering the user about.
 */
export function useAutoSnapshot() {
  const { data: meta } = useMetaData()
  const queryClient = useQueryClient()
  const attempted = useRef(false)

  useEffect(() => {
    if (attempted.current) return
    if (!meta) return

    const thisWeek = currentWeekKey()
    const lastSnapshot = meta.lastSnapshotWeek

    // Up to date — nothing to do. Mark attempted so we don't re-check
    // on every cache update.
    if (lastSnapshot === thisWeek) {
      attempted.current = true
      return
    }

    // Snapshot the week that just ended (previous Monday).
    const weekToSnapshot = weekKeyOffset(new Date(), 1)

    // Don't thrash if the last snapshot is already for this target.
    if (lastSnapshot === weekToSnapshot) {
      attempted.current = true
      return
    }

    attempted.current = true
    createWeeklySnapshot(weekToSnapshot)
      .then((result) => {
        if (result?.alreadyExisted) {
          console.info(
            `[snapshot] week ${weekToSnapshot} already archived by another client`
          )
        } else {
          console.info(`[snapshot] archived week ${weekToSnapshot}`)
          // Pull the fresh meta so the archive panel picks up the new entry
          queryClient.invalidateQueries({ queryKey: ['meta'] })
        }
      })
      .catch((err) => {
        console.warn('[snapshot] failed', err)
        // Allow a retry next mount in case the error was transient
        attempted.current = false
      })
  }, [meta, queryClient])
}
